import { useCallback, useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'

import {
  CreateObjectFieldCard,
  SelectableTile,
  SelectableTileGrid,
} from 'entities/kyrgyzstan/ui/create-object'

import { KyrgyzstanCreateObjectApi } from 'shared/api/kyrgyzstan/create-object'
import type { WebviewOwnerRealtyType } from 'shared/api/kyrgyzstan/create-object'
import { useApiErrorDialog } from 'shared/ui/dialog-bottom-sheet/lib'

import type { ObjectTypeStepScreenProps } from '../../types'
import { CreateObjectStepLayout } from '../create-object-step-layout'

import { ObjectTypeStepSkeleton } from './ui/object-type-step-skeleton'

import s from './styles.module.scss'

export const ObjectTypeStep = ({
  draft,
  layoutProps,
  onNext,
  setDraft,
}: ObjectTypeStepScreenProps) => {
  const { t } = useTranslation()
  const { showApiErrorDialog } = useApiErrorDialog()
  const [realtyTypes, setRealtyTypes] = useState<WebviewOwnerRealtyType[]>([])
  const [selectedRealtyTypeId, setSelectedRealtyTypeId] = useState<
    number | null
  >(() => draft?.realty_type_id ?? null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    const abortController = new AbortController()

    const fetchRealtyTypes = async () => {
      setIsLoading(true)

      try {
        const { data } = await KyrgyzstanCreateObjectApi.getRealtyTypes(
          abortController.signal,
        )
        const nextRealtyTypes = data.data

        setRealtyTypes(nextRealtyTypes)
        setSelectedRealtyTypeId((currentRealtyTypeId) => {
          const isCurrentRealtyTypeAvailable =
            currentRealtyTypeId !== null &&
            nextRealtyTypes.some((type) => type.id === currentRealtyTypeId)

          if (isCurrentRealtyTypeAvailable) {
            return currentRealtyTypeId
          }

          return draft?.realty_type_id ?? null
        })
      } catch (error) {
        if (!abortController.signal.aborted) {
          showApiErrorDialog(error)
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false)
        }
      }
    }

    fetchRealtyTypes()

    return () => {
      abortController.abort()
    }
  }, [draft?.realty_type_id, showApiErrorDialog])

  const handleContinue = useCallback(async () => {
    if (selectedRealtyTypeId === null || isLoading || isSubmitting) {
      return
    }

    const draftRealtyTypeId = draft?.realty_type_id ?? null

    if (draftRealtyTypeId === selectedRealtyTypeId) {
      onNext()

      return
    }

    setIsSubmitting(true)
    let shouldGoNext = false

    try {
      const { data } = await KyrgyzstanCreateObjectApi.createDraft({
        realty_type_id: selectedRealtyTypeId,
      })

      setDraft(data.data)

      shouldGoNext = true
    } catch (error) {
      showApiErrorDialog(error)
    } finally {
      setIsSubmitting(false)
    }

    if (shouldGoNext) {
      onNext()
    }
  }, [
    isLoading,
    isSubmitting,
    draft?.realty_type_id,
    onNext,
    selectedRealtyTypeId,
    setDraft,
    showApiErrorDialog,
  ])

  const continueDisabled =
    selectedRealtyTypeId === null || isLoading || isSubmitting

  const hasRealtyTypes = Boolean(realtyTypes.length)
  const shouldShowLoader = isLoading && !hasRealtyTypes
  const shouldShowEmptyMessage = !isLoading && !hasRealtyTypes

  return (
    <CreateObjectStepLayout
      {...layoutProps}
      continueDisabled={continueDisabled}
      continueLoading={isSubmitting}
      onContinue={handleContinue}
      title={t('kyrgyzstan_block.create_object.steps.object_type.title')}
    >
      <CreateObjectFieldCard
        title={t('kyrgyzstan_block.create_object.steps.object_type.helper')}
        padding="horizontal"
      >
        {shouldShowLoader ? (
          <ObjectTypeStepSkeleton />
        ) : (
          <SelectableTileGrid>
            {realtyTypes.map((type) => (
              <SelectableTile
                key={type.id}
                title={type.name}
                icon={type.icon ?? undefined}
                onClick={() => setSelectedRealtyTypeId(type.id)}
                isSelected={selectedRealtyTypeId === type.id}
              />
            ))}
          </SelectableTileGrid>
        )}

        {shouldShowEmptyMessage && (
          <p className={s.message}>{t('kyrgyzstan_block.empty.title')}</p>
        )}
      </CreateObjectFieldCard>
    </CreateObjectStepLayout>
  )
}
